import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPermission } from '../utils/permissionUtils';
import '../styles/activity-log.css';

const ActivityLog = ({ isOpen, onClose }) => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    actionType: '',
    resourceType: '',
    targetUserId: '',
    startDate: '',
    endDate: '',
    success: ''
  });
  const [users, setUsers] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [deviceStats, setDeviceStats] = useState(null);
  const [error, setError] = useState(null);

  // Check if current user can view activity logs
  const canViewLogs = hasPermission(currentUser, 'view-activity-logs');

  useEffect(() => {
    if (isOpen && canViewLogs) {
      loadActivityLogs();
      loadFilterOptions();
    }
  }, [isOpen, canViewLogs, filters]);

  useEffect(() => {
    if (isOpen && canViewLogs) {
      loadDeviceStats();
    }
  }, [isOpen, canViewLogs]);

  const loadDeviceStats = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const url = `${API_BASE}/api/activity-logs?operation=getDeviceStats&userId=${currentUser.id}&days=90`;
      const response = await fetch(url);
      if (response.ok) {
        setDeviceStats(await response.json());
      }
    } catch (error) {
      // Device stats are supplementary; the log itself still works without them
      setDeviceStats(null);
    }
  };

  const loadActivityLogs = async () => {
    if (!canViewLogs) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      queryParams.append('userId', currentUser.id);
      queryParams.append('operation', 'getActivityLogs');

      const url = `${API_BASE}/api/activity-logs?${queryParams.toString()}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch activity logs: ${response.status}`);
      }
      
      const data = await response.json();
      setLogs(data.logs || []);
      setPagination(data.pagination || {});
    } catch (error) {
      console.error('Error loading activity logs:', error);
      setError(error.message);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    if (!canViewLogs) return;
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      // Load users for filtering
      const usersUrl = `${API_BASE}/api/permissions?operation=listUsers&userId=${currentUser.id}`;
      const usersResponse = await fetch(usersUrl);
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users || []);
      }

      // Extract unique types from a sample of logs
      const sampleUrl = `${API_BASE}/api/activity-logs?operation=getActivityLogs&userId=${currentUser.id}&limit=1000`;
      const sampleResponse = await fetch(sampleUrl);
      if (sampleResponse.ok) {
        const sampleData = await sampleResponse.json();
        const sampleLogs = sampleData.logs || [];
        
        const uniqueActionTypes = [...new Set(sampleLogs.map(log => log.action_type))].filter(Boolean).sort();
        const uniqueResourceTypes = [...new Set(sampleLogs.map(log => log.resource_type))].filter(Boolean).sort();
        
        setActionTypes(uniqueActionTypes);
        setResourceTypes(uniqueResourceTypes);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 when changing filters
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 25,
      actionType: '',
      resourceType: '',
      targetUserId: '',
      startDate: '',
      endDate: '',
      success: ''
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (durationMs) => {
    if (!durationMs) return '-';
    if (durationMs < 1000) return `${durationMs}ms`;
    return `${(durationMs / 1000).toFixed(2)}s`;
  };

  const getActionTypeDisplay = (actionType) => {
    if (!actionType) return '-';
    return actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusBadge = (success, errorMessage) => {
    if (success) {
      return <span className="status-success">✓ Success</span>;
    }
    return (
      <span className="status-error" title={errorMessage || 'Error'}>
        ✗ Error
      </span>
    );
  };

  if (!isOpen) return null;

  if (!canViewLogs) {
    return (
      <div className="activity-overlay">
        <div className="activity-modal">
          <div className="activity-header">
            <h2>Activity Log</h2>
            <button className="activity-close" onClick={onClose}>×</button>
          </div>
          <div className="activity-body">
            <div className="no-permission">
              <p>You don't have permission to view activity logs.</p>
              <p>Contact an administrator to request access.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-overlay">
      <div className="activity-modal">
        <div className="activity-header">
          <h2>Activity Log</h2>
          <button className="activity-close" onClick={onClose}>×</button>
        </div>

        <div className="activity-body">
          {/* Mobile vs desktop summary (last 90 days) */}
          {deviceStats && deviceStats.stats && deviceStats.stats.length > 0 && (() => {
            const known = deviceStats.stats.filter(s => s.device !== 'unknown');
            const totalVisitors = known.reduce((sum, s) => sum + s.uniqueVisitors, 0);
            if (totalVisitors === 0) return null;
            const pct = (d) => {
              const row = known.find(s => s.device === d);
              return row ? Math.round((row.uniqueVisitors / totalVisitors) * 100) : 0;
            };
            const visitors = (d) => known.find(s => s.device === d)?.uniqueVisitors || 0;
            return (
              <div className="device-stats" title={`Unique visitors over the last ${deviceStats.periodDays} days, classified by browser user-agent`}>
                <span className="device-stat">📱 Mobile <strong>{pct('mobile')}%</strong> ({visitors('mobile')})</span>
                <span className="device-stat">🖥️ Desktop <strong>{pct('desktop')}%</strong> ({visitors('desktop')})</span>
                <span className="device-stats-period">last {deviceStats.periodDays} days, by unique visitor</span>
              </div>
            );
          })()}

          {/* Compact Filters */}
          <div className="activity-filters">
            <div className="filter-row">
              <select 
                value={filters.actionType} 
                onChange={(e) => handleFilterChange('actionType', e.target.value)}
              >
                <option value="">All Actions</option>
                {actionTypes.map(type => (
                  <option key={type} value={type}>
                    {getActionTypeDisplay(type)}
                  </option>
                ))}
              </select>

              <select 
                value={filters.resourceType} 
                onChange={(e) => handleFilterChange('resourceType', e.target.value)}
              >
                <option value="">All Resources</option>
                {resourceTypes.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>

              <select 
                value={filters.targetUserId} 
                onChange={(e) => handleFilterChange('targetUserId', e.target.value)}
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.display_name}
                  </option>
                ))}
              </select>

              <select 
                value={filters.success} 
                onChange={(e) => handleFilterChange('success', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="true">Success Only</option>
                <option value="false">Errors Only</option>
              </select>

              <button onClick={handleClearFilters} className="clear-btn">
                Clear
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="activity-content">
            {error && (
              <div className="error-msg">
                Error: {error}
              </div>
            )}

            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Loading...</p>
              </div>
            ) : (
              <>
                {/* Activity table */}
                <div className="table-container">
                  <table className="activity-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>User</th>
                        <th>Action</th>
                        <th>Resource</th>
                        <th>Description</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="no-data">
                            {error ? 'No results found' : 'No activity data found'}
                          </td>
                        </tr>
                      ) : (
                        logs.map((log) => (
                          // data-label drives the stacked card layout on mobile,
                          // where the table collapses to one card per entry
                          <tr key={log.id} className={!log.success ? 'error-row' : ''}>
                            <td className="time" data-label="Time">
                              {formatTimestamp(log.created_at)}
                            </td>
                            <td className="user" data-label="User">
                              <div>
                                <div className="user-name">{log.display_name || log.user_id}</div>
                                {log.email && <div className="user-email">{log.email}</div>}
                              </div>
                            </td>
                            <td className="action" data-label="Action">
                              {getActionTypeDisplay(log.action_type)}
                            </td>
                            <td className="resource" data-label="Resource">
                              <div>
                                <div className="res-type">{log.resource_type || '-'}</div>
                                {log.resource_id && (
                                  <div className="res-id">{log.resource_id}</div>
                                )}
                              </div>
                            </td>
                            <td className="desc" data-label="Description">
                              {log.description || '-'}
                              {log.duration_ms && (
                                <div className="duration">({formatDuration(log.duration_ms)})</div>
                              )}
                            </td>
                            <td className="status" data-label="Status">
                              {getStatusBadge(log.success, log.error_message)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      disabled={pagination.currentPage <= 1}
                      onClick={() => handleFilterChange('page', pagination.currentPage - 1)}
                    >
                      ← Previous
                    </button>
                    
                    <span>
                      Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    
                    <button 
                      disabled={pagination.currentPage >= pagination.totalPages}
                      onClick={() => handleFilterChange('page', pagination.currentPage + 1)}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;